(() => {
  const { CustomHTMLElement, DrawerContent } = window.ThemeCore || {};
  if (!CustomHTMLElement || !DrawerContent) return;

  // Event contract (phase 1 - preserved):
  // - cart:updated { cart }
  // - cart:refresh { cart, openMiniCart?, replacementDelay? }
  // - variant:added { variant }
  const CART_EVENTS = {
    UPDATED: "cart:updated",
    REFRESH: "cart:refresh",
    VARIANT_ADDED: "variant:added",
  };

  const defineCE = (name, ctor, options) => {
    if (!window.customElements.get(name)) {
      window.customElements.define(name, ctor, options);
    }
  };

  const ROUTES = {
    cartUrl: () =>
      window.themeVariables?.routes?.cartUrl || `${window.Shopify?.routes?.root || "/"}cart`,
    cartChangeUrl: () => window.themeVariables?.routes?.cartChangeUrl || "/cart/change",
    cartUpdateUrl: () => `${ROUTES.cartUrl()}/update`,
  };

  const CART_AJAX_ERROR_SVG = `<svg width="14" height="14" viewBox="0 0 18 18" aria-hidden="true" focusable="false">
    <path d="M0 9C0 4.02944 4.02944 0 9 0C13.9706 0 18 4.02944 18 9C18 13.9706 13.9706 18 9 18C4.02944 18 0 13.9706 0 9Z" fill="#eb001b"></path>
    <path d="M5.29289 6.70711L11.2929 12.7071L12.7071 11.2929L6.70711 5.29289L5.29289 6.70711ZM6.70711 12.7071L12.7071 6.70711L11.2929 5.2929L5.29289 11.2929L6.70711 12.7071Z" fill="#ffffff"></path>
  </svg>`;

  async function parseJSONResponse(response, fallbackMessage) {
    let data = null;
    try {
      data = await response.json();
    } catch (_) {
      throw new Error(fallbackMessage);
    }
    if (!response.ok) {
      const raw = data?.description || data?.message || fallbackMessage;
      const localize = window.ThemeCore?.ATC?.localizeCartErrorMessage;
      throw new Error(typeof localize === "function" ? localize(raw) : raw);
    }
    return data;
  }

  function createCartAjaxErrorElement(message) {
    const errorEl = document.createElement("p");
    errorEl.className = "cart-ajax-error";
    errorEl.setAttribute("role", "alert");
    errorEl.innerHTML = CART_AJAX_ERROR_SVG;
    const text = document.createElement("span");
    text.textContent = message;
    errorEl.appendChild(text);
    return errorEl;
  }

  function clearLineItemCartError(lineItemElement) {
    lineItemElement?.querySelectorAll(".cart-ajax-error").forEach((el) => el.remove());
  }

  function showLineItemCartError(lineItemElement, message) {
    if (!lineItemElement || !message) return;

    clearLineItemCartError(lineItemElement);

    const errorEl = createCartAjaxErrorElement(message);
    const quantityWrapper = lineItemElement.querySelector(
      ".line-item__quantity-wrapper, .quantity-with-price-wrapper"
    );
    const info = lineItemElement.querySelector(".line-item__info .product-item-meta");

    if (quantityWrapper) {
      quantityWrapper.insertAdjacentElement("afterend", errorEl);
    } else if (info) {
      info.appendChild(errorEl);
    } else {
      lineItemElement.appendChild(errorEl);
    }
  }

  async function postCartChange(body) {
    const response = await fetch(`${ROUTES.cartChangeUrl()}.js`, {
      method: "POST",
      body: JSON.stringify(body),
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        "Accept-Language": document.documentElement.lang || window.Shopify?.locale || "",
      },
    });
    return parseJSONResponse(response, "Failed to update cart.");
  }

  async function postCartUpdate(body) {
    const response = await fetch(`${ROUTES.cartUpdateUrl()}.js`, {
      method: "POST",
      body: JSON.stringify(body),
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "Accept-Language": document.documentElement.lang || window.Shopify?.locale || "",
      },
    });
    return parseJSONResponse(response, "Failed to update cart.");
  }

  async function getCartSectionHtml(sectionId) {
    const response = await fetch(`${ROUTES.cartUrl()}?section_id=${sectionId}`);
    if (!response.ok) {
      throw new Error("Failed to fetch cart section.");
    }
    return response.text();
  }

  function dispatchVariantAdded(variant) {
    document.dispatchEvent(
      new CustomEvent(CART_EVENTS.VARIANT_ADDED, {
        bubbles: true,
        detail: { variant },
      })
    );
  }

  function dispatchCartUpdated(cart) {
    document.documentElement.dispatchEvent(
      new CustomEvent(CART_EVENTS.UPDATED, { bubbles: true, detail: { cart } })
    );
  }

  function dispatchCartRefresh(cart, options = {}) {
    document.documentElement.dispatchEvent(
      new CustomEvent(CART_EVENTS.REFRESH, {
        bubbles: true,
        detail: { cart, ...options },
      })
    );
  }

  function itemCountFromCartLike(cart) {
    if (!cart || typeof cart !== "object") return null;
    if (typeof cart.item_count === "number") return cart.item_count;
    if (Array.isArray(cart.items)) {
      return cart.items.reduce((sum, li) => sum + (parseInt(li.quantity, 10) || 0), 0);
    }
    return null;
  }

  function setLineItemLoaderState(lineItemElement, isLoading, isSuccess = false) {
    const loader = lineItemElement?.querySelector(".line-item__loader");
    const loaderSpinner = loader?.querySelector(".line-item__loader-spinner");
    const loaderMark = loader?.querySelector(".line-item__loader-mark");

    if (!loader || !loaderSpinner) return;

    loader.hidden = !isLoading && !isSuccess;
    loaderSpinner.hidden = !isLoading;

    if (loaderMark) {
      loaderMark.hidden = !isSuccess;
    }
  }

  async function updateMiniCartSellingPlan(lineItemElement, lineItem, sellingPlanId, selectElement) {
    if (!lineItemElement) return;

    setLineItemLoaderState(lineItemElement, true, false);

    const quantityInput = lineItemElement.querySelector(".quantity-selector__input");
    const currentQuantity = quantityInput ? parseInt(quantityInput.value, 10) : 1;

    const requestBody = {
      line: parseInt(lineItem, 10),
      quantity: Number.isNaN(currentQuantity) ? 1 : currentQuantity,
      sections: ["mini-cart"],
      selling_plan: sellingPlanId || "",
    };

    try {
      const cartContent = await postCartChange(requestBody);

      dispatchVariantAdded(
        cartContent.hasOwnProperty("items") ? cartContent.items[0] : cartContent
      );
      dispatchCartUpdated(cartContent);

      setLineItemLoaderState(lineItemElement, false, true);

      dispatchCartRefresh(cartContent, { replacementDelay: 750 });

      if (window.location.pathname === "/cart") {
        const sectionElement =
          lineItemElement.closest('[id^="shopify-section-"]') ||
          document.querySelector('[id^="shopify-section-main-cart"]') ||
          document.querySelector('[id^="shopify-section-"]');

        const sectionId = sectionElement?.id;
        if (sectionId) {
          const sectionName = sectionId.replace("shopify-section-", "");
          getCartSectionHtml(sectionName)
            .then((html) => {
              const parser = new DOMParser();
              const doc = parser.parseFromString(html, "text/html");
              const newSection = doc.querySelector(`#${sectionId}`);
              const currentSection = document.querySelector(`#${sectionId}`);

              if (newSection && currentSection) {
                currentSection.innerHTML = newSection.innerHTML;
              } else {
                window.location.reload();
              }
            })
            .catch(() => {
              window.location.reload();
            });
        } else {
          window.location.reload();
        }
      }

      setTimeout(() => {
        const loader = lineItemElement.querySelector(".line-item__loader");
        if (loader) loader.hidden = true;
      }, 1000);
    } catch (error) {
      console.error("Error updating selling plan:", error);
      if (selectElement && selectElement.dataset.originalValue !== undefined) {
        selectElement.value = selectElement.dataset.originalValue;
      }
      setLineItemLoaderState(lineItemElement, false, false);
      showLineItemCartError(lineItemElement, error?.message);
    }
  }

  function storeSellingPlanOriginalValues(root = document) {
    const sellingPlanSelects = root.querySelectorAll(".mini-cart-selling-plan .selling-plan-select");
    sellingPlanSelects.forEach((select) => {
      select.dataset.originalValue = select.value;
    });
  }

  function setupGlobalSellingPlanHandlers() {
    if (document.documentElement.dataset.sellingPlanHandlersBound === "true") return;
    document.documentElement.dataset.sellingPlanHandlersBound = "true";

    document.addEventListener("click", (event) => {
      const toggleBundleButton = event.target.closest(".toggle-bundle-content");
      if (toggleBundleButton) {
        const wrapper = toggleBundleButton.closest(".bundle-properties-wrapper");
        const list = wrapper?.querySelector(".bundle-properties");
        if (!list) return;

        const isExpanded = toggleBundleButton.getAttribute("aria-expanded") === "true";
        list.classList.toggle("hidden");
        toggleBundleButton.setAttribute("aria-expanded", String(!isExpanded));
        toggleBundleButton.textContent = isExpanded ? "Inhalt anzeigen" : "Inhalt ausblenden";
        return;
      }

      const convertButton = event.target.closest(".selling-plan-convert-btn");
      if (!convertButton) return;

      const lineItemElement = convertButton.closest(".line-item");
      const productId = lineItemElement?.dataset.productId;
      if (!productId) {
        console.error("Could not find product ID for selling plan conversion");
        return;
      }

      const lineItem = convertButton.dataset.lineItem;
      const firstSellingPlanId = convertButton.dataset.firstSellingPlanId;
      if (!firstSellingPlanId) {
        console.error("No selling plans found for this product");
        return;
      }

      updateMiniCartSellingPlan(lineItemElement, lineItem, firstSellingPlanId);
    });

    document.addEventListener("change", (event) => {
      const selectElement = event.target.closest(".mini-cart-selling-plan .selling-plan-select");
      if (!selectElement) return;

      const lineItemElement = selectElement.closest(".line-item");
      const lineItem = selectElement.dataset.lineItem;
      updateMiniCartSellingPlan(lineItemElement, lineItem, selectElement.value, selectElement);
    });

    document.addEventListener("DOMContentLoaded", () => {
      storeSellingPlanOriginalValues(document);
    });

    document.documentElement.addEventListener("cart:refresh", () => {
      storeSellingPlanOriginalValues(document);
    });
  }

  class CartCount extends CustomHTMLElement {
    connectedCallback() {
      this.rootDelegate.on(CART_EVENTS.UPDATED, (event) => {
        const itemsCount = itemCountFromCartLike(event?.detail?.cart);
        if (typeof itemsCount !== "number") return;
        this.innerText = itemsCount;
      });
    }
  }
  defineCE("cart-count", CartCount);

  class CartDrawer extends DrawerContent {
    connectedCallback() {
      super.connectedCallback();
      this.nextReplacementDelay = 0;
      this.rootDelegate.on(CART_EVENTS.REFRESH, this._rerenderCart.bind(this));
      this.addEventListener(CART_EVENTS.VARIANT_ADDED, () => (this.nextReplacementDelay = 600));
      this._storeSellingPlanOriginalValues();
    }

    async _rerenderCart(event) {
      let cartContent = null;
      let html = "";

      if (event.detail?.cart?.sections) {
        cartContent = event.detail.cart;
        html = event.detail.cart.sections["mini-cart"];
      } else {
        html = await getCartSectionHtml(this.getAttribute("section"));
      }

      const fakeDiv = document.createElement("div");
      fakeDiv.innerHTML = html;

      setTimeout(() => {
        const previousPosition = this.querySelector(".drawer__content")?.scrollTop || 0;
        const next = fakeDiv.querySelector("cart-drawer");
        if (next) {
          this.innerHTML = next.innerHTML;
          this._storeSellingPlanOriginalValues();
        }


        const drawerContent = this.querySelector(".drawer__content");
        if (drawerContent && !(cartContent && cartContent["item_count"] === 0)) {
          drawerContent.scrollTop = previousPosition;
        }

        if (event.detail?.openMiniCart) {
          this.clientWidth;
          this.open = true;
        }
      }, event.detail?.replacementDelay || this.nextReplacementDelay);

      this.nextReplacementDelay = 0;
    }

    attributeChangedCallback(name, oldValue, newValue) {
      super.attributeChangedCallback(name, oldValue, newValue);
      if (name === "open" && this.open) {
        const drawerContent = this.querySelector(".drawer__content");
        if (drawerContent) drawerContent.scrollTop = 0;
      }
    }

    _storeSellingPlanOriginalValues() {
      storeSellingPlanOriginalValues(this);
    }
  }
  defineCE("cart-drawer", CartDrawer);
  setupGlobalSellingPlanHandlers();

  class CartNote extends HTMLTextAreaElement {
    connectedCallback() {
      this.addEventListener("change", this._onNoteChanged.bind(this));
    }
    get ownedToggle() {
      return this.hasAttribute("aria-owns")
        ? document.getElementById(this.getAttribute("aria-owns"))
        : null;
    }
    async _onNoteChanged() {
      if (this.ownedToggle) {
        this.ownedToggle.innerHTML =
          this.value === ""
            ? window.themeVariables.strings.cartAddOrderNote
            : window.themeVariables.strings.cartEditOrderNote;
      }
      await postCartUpdate({ note: this.value });
    }
  }
  defineCE("cart-note", CartNote, { extends: "textarea" });

  class LineItemQuantity extends CustomHTMLElement {
    connectedCallback() {
      this.delegate.on("click", "a", this._onQuantityLinkClicked.bind(this));
      this.delegate.on("change", "input", this._onQuantityChanged.bind(this));
    }
    _onQuantityLinkClicked(event, target) {
      event.preventDefault();
      this._updateFromLink(target.href);
    }
    _onQuantityChanged(event, target) {
      this._updateFromLink(
        `${window.themeVariables.routes.cartChangeUrl}?quantity=${target.value}&line=${target.getAttribute("data-line")}`
      );
    }
    _resetQuantityInput() {
      const quantityInput = this.querySelector(".quantity-selector__input");
      if (quantityInput) {
        quantityInput.value = quantityInput.defaultValue;
      }
    }
    async _updateFromLink(link) {
      const changeUrl = new URL(link, `https://${window.themeVariables.routes.host}`);
      const searchParams = changeUrl.searchParams;
      const line = searchParams.get("line");
      const id = searchParams.get("id");
      const quantity = parseInt(searchParams.get("quantity"), 10);

      this.dispatchEvent(
        new CustomEvent("line-item-quantity:change:start", {
          bubbles: true,
          detail: { newLineQuantity: quantity },
        })
      );

      try {
        const cartContent = await postCartChange({ line, id, quantity, sections: ["mini-cart"] });

        if (window.themeVariables.settings.pageType === "cart") {
          window.location.reload();
          return;
        }

        this.dispatchEvent(
          new CustomEvent("line-item-quantity:change:end", {
            bubbles: true,
            detail: { cart: cartContent, newLineQuantity: quantity },
          })
        );

        dispatchCartUpdated(cartContent);
        dispatchCartRefresh(cartContent, { replacementDelay: quantity === 0 ? 600 : 750 });
      } catch (error) {
        this._resetQuantityInput();
        showLineItemCartError(this.closest(".line-item, line-item, tr"), error?.message);
        this.dispatchEvent(
          new CustomEvent("line-item-quantity:change:error", {
            bubbles: true,
            detail: { message: error?.message },
          })
        );
      }
    }
  }
  defineCE("line-item-quantity", LineItemQuantity);

  class LineItem extends HTMLElement {
    connectedCallback() {
      this.lineItemLoader = this.querySelector(".line-item__loader");
      this.addEventListener(
        "line-item-quantity:change:start",
        this._onQuantityStart.bind(this)
      );
      this.addEventListener(
        "line-item-quantity:change:end",
        this._onQuantityEnd.bind(this)
      );
      this.addEventListener(
        "line-item-quantity:change:error",
        this._onQuantityError.bind(this)
      );
    }
    _onQuantityStart() {
      clearLineItemCartError(this);
      if (!this.lineItemLoader) return;
      this.lineItemLoader.hidden = false;
      this.lineItemLoader.firstElementChild.hidden = false;
      this.lineItemLoader.lastElementChild.hidden = true;
    }
    async _onQuantityEnd(event) {
      if (event.detail.cart["item_count"] === 0) return;
      if (!this.lineItemLoader) return;

      this.lineItemLoader.firstElementChild.hidden = true;
      if (event.detail.newLineQuantity === 0) {
        this.remove();
      } else {
        this.lineItemLoader.hidden = true;
      }
    }
    _onQuantityError() {
      if (!this.lineItemLoader) return;
      this.lineItemLoader.hidden = true;
      this.lineItemLoader.firstElementChild.hidden = true;
      if (this.lineItemLoader.lastElementChild) {
        this.lineItemLoader.lastElementChild.hidden = true;
      }
    }
  }
  defineCE("line-item", LineItem);

})();

